import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Card, Button, Typography, Skeleton, Divider } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";

import useShipments from "../../hooks/useShipments";
import { selectCurrentUser, selectActiveTin } from "../../redux/slices/authSlice";
import ShipmentForm from "../../components/shipments/ShipmentForm";

// Same numbered-section icon badge + title/subtitle bars as
// ShipmentForm's own SectionHeader, skeletonized.
const SectionHeaderSkeleton = ({ titleWidth = 160, subtitleWidth = 260 }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2.5 }}>
    <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: 1.5, flexShrink: 0 }} />
    <Box>
      <Skeleton width={titleWidth} height={20} />
      <Skeleton width={subtitleWidth} height={16} />
    </Box>
  </Box>
);

const FieldSkeleton = (props) => (
  <Skeleton variant="rounded" height={56} sx={{ borderRadius: 1 }} {...props} />
);

// Mirrors ShipmentForm's actual edit-mode layout — same grid columns,
// same dividers between sections, same dropzone footprint — so the loading
// state reads as "this exact form is about to appear," not a generic
// placeholder shape.
const ShipmentFormSkeleton = () => (
  <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
    {/* Section 1: Order & Vendor */}
    <SectionHeaderSkeleton titleWidth={150} subtitleWidth={280} />
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 120px" },
        gap: 2,
        mb: 2,
      }}
    >
      <FieldSkeleton />
      <FieldSkeleton />
      <FieldSkeleton />
    </Box>
    <FieldSkeleton />

    <Divider sx={{ my: 3 }} />

    {/* Section 2: Package & Invoice */}
    <SectionHeaderSkeleton titleWidth={180} subtitleWidth={240} />
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
        gap: 2,
        mb: 2,
      }}
    >
      <FieldSkeleton />
      <FieldSkeleton />
      <FieldSkeleton />
    </Box>
    <Skeleton variant="rounded" height={80} sx={{ borderRadius: 1.5 }} />

    <Divider sx={{ my: 3 }} />

    {/* Section 4 (items) — numbered 3 in edit mode, since the promo section
        is create-only */}
    <SectionHeaderSkeleton titleWidth={170} subtitleWidth={220} />
    {[0, 1].map((i) => (
      <Box
        key={i}
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr 1fr 1fr auto" },
          gap: 2,
          mb: 1.5,
          alignItems: "center",
        }}
      >
        <FieldSkeleton height={48} />
        <FieldSkeleton height={48} />
        <FieldSkeleton height={48} />
        <FieldSkeleton height={48} />
        <Skeleton variant="circular" width={36} height={36} />
      </Box>
    ))}
    <Skeleton variant="rounded" width={140} height={32} sx={{ borderRadius: 1, mt: 0.5 }} />

    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 4 }}>
      <Skeleton variant="rounded" width={90} height={36} sx={{ borderRadius: 1 }} />
      <Skeleton variant="rounded" width={130} height={36} sx={{ borderRadius: 1 }} />
    </Box>
  </Box>
);

// The edit-shipment form, as its own page (/dashboard/shipment/:id/edit)
// instead of a modal — same ShipmentForm component, same fields,
// same validation, same update logic as before (see CustomerShipments.jsx's
// now-removed inline edit dialog, which this mirrors exactly).
// ShipmentForm itself no longer has a Dialog-mode at all — every caller
// renders it inline like this now.
const CustomerEditShipment = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const user = useSelector(selectCurrentUser);
  const activeTin = useSelector(selectActiveTin);
  const { current, loading, loadOne, update, clear } = useShipments();

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadOne(id);
    return () => clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleUpdate = async (data) => {
    setSaving(true);
    const result = await update(id, data);
    setSaving(false);
    if (result) {
      navigate("/dashboard/shipments");
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/dashboard/shipments"))}
          variant="outlined"
          size="small"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" fontWeight={700} color="text.primary">
            Edit Shipment
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {current?.trackingNumber ? `Tracking # ${current.trackingNumber}` : "Update this shipment's details"}
          </Typography>
        </Box>
      </Box>

      <Card sx={{ p: { xs: 1.5, sm: 3 } }}>
        {loading && !current ? (
          <ShipmentFormSkeleton />
        ) : (
          <ShipmentForm
            open
            onClose={() => navigate("/dashboard/shipments")}
            onSubmit={handleUpdate}
            initialData={current}
            loading={saving}
            currentUser={user}
            activeTin={activeTin}
          />
        )}
      </Card>
    </Box>
  );
};

export default CustomerEditShipment;
